import cloneDeep from 'clone-deep'
import type { InputType } from 'config'
import { useEffect, useMemo, useState } from 'react'
import { confirmable, createConfirmation, ReactConfirmProps } from 'react-confirm'
import { Modal, RadioGroup } from 'stories/components'
import { InputValidate } from 'utils'

const allGenerateOptions = {
  generate: 'Generate and Save to Document Storage',
  download: 'Just download document',
  email: 'Email to Borrower',
}

const defaultInputs = (options: Record<string, string>): Record<string, InputType> => {
  return {
    option: {
      inputType: 'radio',
      title: 'Generate options',
      options,
      value: 'download',
    },
  }
}

interface ConfirmGenerateDialogProps {
  /**
   * Title
   */
  content?: string | JSX.Element
  /**
   * Okay button handler
   */
  onOk?: (arg: string) => void
  /**
   * Cancel button handler
   */
  onCancel?: Function
  /**
   * Is Open
   */
  isOpen?: boolean
  lastUpdatedAt: number
  /**
   * When false, hides Email to Borrower
   */
  showEmailOption?: boolean
}

const ConfirmGenerateDialog = ({
  content = '',
  onOk = () => {},
  onCancel = () => {},
  isOpen: parentIsOpen = false,
  lastUpdatedAt,
  showEmailOption = true,
}: ConfirmGenerateDialogProps) => {
  const options = useMemo(() => {
    if (showEmailOption) return allGenerateOptions
    return Object.fromEntries(Object.entries(allGenerateOptions).filter(([key]) => key !== 'email'))
  }, [showEmailOption])

  const [inputs, setInputs] = useState(defaultInputs(options))

  useEffect(() => {
    setInputs(defaultInputs(options))
  }, [options])

  const onChange = (key: string, value: string) => {
    const newInputs = cloneDeep(inputs)
    newInputs[key].value = value
    newInputs[key].error = ''
    setInputs(newInputs)
  }

  const onConfirm = async () => {
    let hasError = false
    const newStats = cloneDeep(inputs)
    const inputData: Record<string, any> = {}
    for (const key in newStats) {
      const { value } = newStats[key]
      let error = InputValidate(newStats[key])
      newStats[key].error = error
      if (error.length > 0) hasError = true

      if (value !== undefined) inputData[key] = value
    }
    if (hasError) {
      setInputs(newStats)
      return
    }

    onOk(inputData.option)
    onCancel()
  }

  return (
    <Modal
      isOpen={parentIsOpen}
      lastUpdatedAt={lastUpdatedAt}
      title={content}
      titleOkay="Continue"
      disabled={!inputs.option.value}
      onClose={() => onCancel()}
      onOk={onConfirm}
    >
      <div className="w-full max-w-[500px] overflow-hidden text-md text-gray-600">
        {Object.keys(inputs).map((key, index) => {
          const input = inputs[key]
          return (
            <div key={index} className="input">
              <RadioGroup
                id="option"
                title={input.title}
                options={options}
                error={input.error}
                value={input.value as string}
                required
                onChange={(value) => onChange('option', value)}
              />
            </div>
          )
        })}
      </div>
    </Modal>
  )
}

const ConfirmDialog = ({ show, proceed, promptation, showEmailOption = true }: ReactConfirmProps | any) => {
  const [lastUpdatedAt, setLastUpdatedAt] = useState(Date.now())
  useEffect(() => {
    setLastUpdatedAt(Date.now())
  }, [show])

  return (
    <ConfirmGenerateDialog
      isOpen={show}
      onOk={(result: string) => proceed(result as any)}
      onCancel={() => proceed(false as any)}
      lastUpdatedAt={lastUpdatedAt}
      content={promptation}
      showEmailOption={showEmailOption}
    />
  )
}

const confirmDlg = confirmable(ConfirmDialog)

const createConfirm = createConfirmation(confirmDlg)

export const confirmGenerate = (
  content: string | JSX.Element,
  options?: { showEmailOption?: boolean },
) => createConfirm({ promptation: content, showEmailOption: options?.showEmailOption ?? true })
